import React, { useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import Tabs from '@cloudscape-design/components/tabs';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { AppArea } from '../services/types';

/** Tab identifiers for the primary navigation. */
export type AppTab = 'asn' | 'promised' | 'leadtime';

const NAV_ITEMS: { id: AppTab; label: string }[] = [
    { id: 'asn', label: 'ASN' },
    { id: 'promised', label: 'Promised Date' },
    { id: 'leadtime', label: 'Lead Time' },
];

/** Presentation for each top-level area in the navigation drawer. */
const AREA_META: Record<AppArea, { label: string; description: string }> = {
    connect: {
        label: 'Supplier Gateway',
        description: 'Submit ASNs, promised dates, and lead times',
    },
    control: {
        label: 'Supplier Control',
        description: 'Manage supplier access and approvals',
    },
    insights: {
        label: 'Supplier Insights',
        description: 'Performance reporting across suppliers',
    },
};

interface AppShellProps {
    children: React.ReactNode;
    userEmail?: string;
    onSignOut?: () => void;
    /** Optional actions rendered above the content (e.g. integration guide). */
    navActions?: React.ReactNode;
    /** Active primary-nav tab. When provided, the tab row is shown. */
    tab?: AppTab;
    onTabChange?: (tab: AppTab) => void;
    /** Areas the user can access. A left nav appears when > 1. */
    areas?: AppArea[];
    activeArea?: AppArea;
    onAreaChange?: (area: AppArea) => void;
    /** When provided, clicking the brand/title returns to the home view. */
    onHome?: () => void;
}

/**
 * Branded application shell built on Cloudscape AppLayout: a TopNavigation bar
 * (brand + user identity + sign out), an optional left SideNavigation for
 * multi-area users, a primary-nav tab row, and the page content. Behavior
 * mirrors the previous shell; only the design system changed.
 */
export default function AppShell({
    children,
    userEmail,
    onSignOut,
    navActions,
    tab,
    onTabChange,
    areas,
    activeArea,
    onAreaChange,
    onHome,
}: AppShellProps) {
    const multiArea = (areas?.length ?? 0) > 1;
    // Open the workspace rail by default for multi-area users so it's visible
    // on entry; the hamburger (rendered by AppLayout) toggles it from there.
    const [navOpen, setNavOpen] = useState(multiArea);

    return (
        <>
            <div id="top-nav">
                <TopNavigation
                    identity={{
                        href: '#',
                        // Just the org name on the banner. The product/area
                        // context (Supplier Gateway, and Connect/Control/
                        // Insights) lives in the left nav and page headers.
                        title: 'Tech Supply Chain',
                        onFollow: (e) => {
                            e.preventDefault();
                            onHome?.();
                        },
                    }}
                    utilities={
                        userEmail && onSignOut
                            ? [
                                  {
                                      type: 'menu-dropdown',
                                      text: userEmail,
                                      iconName: 'user-profile',
                                      items: [{ id: 'signout', text: 'Sign out' }],
                                      onItemClick: ({ detail }) => {
                                          if (detail.id === 'signout') onSignOut();
                                      },
                                  },
                              ]
                            : []
                    }
                />
            </div>
            <AppLayout
                headerSelector="#top-nav"
                toolsHide
                navigationHide={!multiArea}
                navigationOpen={navOpen}
                onNavigationChange={({ detail }) => setNavOpen(detail.open)}
                navigation={
                    <SideNavigation
                        header={{ text: 'Workspaces', href: `#${activeArea ?? ''}` }}
                        activeHref={`#${activeArea ?? ''}`}
                        onFollow={(e) => {
                            e.preventDefault();
                            const next = e.detail.href.replace(/^#/, '');
                            if (next && next !== activeArea) {
                                onAreaChange?.(next as AppArea);
                            }
                        }}
                        items={(areas ?? []).map((a) => ({
                            type: 'link',
                            text: AREA_META[a].label,
                            href: `#${a}`,
                        }))}
                    />
                }
                content={
                    <SpaceBetween size="l">
                        {navActions ? <Box textAlign="right">{navActions}</Box> : null}
                        {tab ? (
                            <Tabs
                                activeTabId={tab}
                                onChange={({ detail }) =>
                                    onTabChange?.(detail.activeTabId as AppTab)
                                }
                                tabs={NAV_ITEMS.map((item) => ({
                                    id: item.id,
                                    label: item.label,
                                    content: item.id === tab ? children : undefined,
                                }))}
                            />
                        ) : (
                            children
                        )}
                    </SpaceBetween>
                }
            />
        </>
    );
}

AppShell.defaultProps = {
    userEmail: undefined,
    onSignOut: undefined,
    navActions: undefined,
    tab: undefined,
    onTabChange: undefined,
    areas: undefined,
    activeArea: undefined,
    onAreaChange: undefined,
    onHome: undefined,
};
